import fetchRetry from 'fetch-retry';
import { useLogto } from '@logto/react';

const originalFetch = globalThis.fetch;

/**
 * Utility function to make requests to API so it retries x number of times
 */
const retryFetch = fetchRetry(originalFetch, {
	retries: parseInt(import.meta.env.VITE_API_RETRIES) || 2,
	retryDelay: parseInt(import.meta.env.VITE_API_RETRY_DELAY) || 3000,
});

export const createAPIHeaders = (apiKey: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'): HeadersInit => {
	const baseHeaders: HeadersInit = {
		Accept: '*/*',
		authorization: `Bearer ${apiKey}`,
	};
	if (method !== 'GET' && method !== 'DELETE') {
		baseHeaders['Content-Type'] = 'application/json';
	}
	return baseHeaders;
};

/**
 * Synchronously call an API function, getting new accessToken from LogTo if the current one is expired
 *
 * @param logtoStore LogTo's internal data store, called with useLogto()
 * @param apiFunction The function to be called with the accessToken as an argument
 */
export const callAPISync = (logtoStore: ReturnType<typeof useLogto>, apiFunction: (accessToken: string) => void): void => {
	logtoStore.getAccessToken(import.meta.env.VITE_LOGTO_IDENTIFIER).then((token) => {
		if (token) {
			apiFunction(token);
		} else if (process.env.NODE_ENV === 'test') {
			apiFunction('');
		} else {
			logtoStore.signOut();
		}
	});
};

/**
 * Asynchronously call an API function, getting new accessToken from LogTo if the current one is expired
 *
 * @param logtoStore LogTo's internal data store, called with useLogto()
 * @param apiFunction The function to be called with the accessToken as an argument
 * @returns The return of the original function. Can be undefined, but this can usually be ignored as that means the user was logged out
 */
export async function callAPIAsync<T>(
	logtoStore: ReturnType<typeof useLogto>,
	apiFunction: (accessToken: string) => Promise<T>
): Promise<T | undefined> {
	const token = await logtoStore.getAccessToken(import.meta.env.VITE_LOGTO_IDENTIFIER);
	if (token) {
		return await apiFunction(token);
	} else if (process.env.NODE_ENV === 'test') {
		return await apiFunction('');
	} else {
		logtoStore.signOut();
	}
}

export const fetchUtility = async (url: string, params: { headers: HeadersInit; method: string; body?: string }): Promise<Response> => {
	try {
		return await retryFetch(url, {
			...params,
		});
	} catch {
		throw new Error('Cannot connect to API');
	}
};

export * from './delete';
export * from './get';
export * from './log';
export * from './pipeline';
export * from './post';
export * from './users';
export * from './patch';
